import './DodMarketFilterTypePicker.scss';
import { useState } from 'react';
import {
    DodFilterTypeOptionGroup,
    DodFilterTypePicker,
    DodFilterTypePickerProps
} from '@/components/DodConfigEditor/common';
import { DodFilters } from '@/types/DodRun';
import {DodFilterType, DodMarketFilterType} from '@/components/DodConfigEditor/types';
import { ByzzerChangeEventHandler, ByzzerTabs } from '@byzzer/ui-components';
import classnames from 'classnames';
import { SAVED_MARKET_INFO } from '@/components/DodConfigEditor/builders/DodMarketFilterBuilder/MarketConstants';
import { DodPreset } from '@/types/ApiTypes';
import DodMarketAdvancedSearch from '../DodMarketAdvancedSearch/DodMarketAdvancedSearch';
import { AdvancedSearchFilters, MarketTab, MarketTabList } from '../DodMarketFilterBuilder';
import { useUser } from '@/contexts/UserContext';

const baseClassName = 'dod-market-filter-type-picker';

export type DodMarketFilterTypeValue = {
    type: DodMarketFilterType;
    value?: string;
};

export type DodMarketFilterTypePickerProps = {
    name?: string;
    value: DodFilterType<DodMarketFilterType>;
    onChange: ByzzerChangeEventHandler<DodFilterType<DodMarketFilterType>>;
    filters: DodFilters;
    tipDelay?: number | [number | null, number | null];
    onPresetSelect: ByzzerChangeEventHandler<DodPreset>;
    advancedSearchFilters: AdvancedSearchFilters;
    onAdvancedSearchFiltersChange: ByzzerChangeEventHandler<AdvancedSearchFilters>;
    activeMarketFolderTab: MarketTab;
    tabList: MarketTabList;
    onTabChange: (activeKey: MarketTab) => void;
    accessibleMarketFolders: any
} & Partial<Omit<DodFilterTypePickerProps<DodFilters, DodFilterType<DodMarketFilterType>>, 'initialOptionGroups'>>;

export function DodMarketFilterTypePicker({
    className,
    onChange,
    filters,
    advancedSearchFilters,
    onAdvancedSearchFiltersChange,
    activeMarketFolderTab,
    tabList,
    onTabChange,
    accessibleMarketFolders,
    ...props
}: DodMarketFilterTypePickerProps) {
    const { features } = useUser();
    const [optionGroups] = useState<DodFilterTypeOptionGroup<DodFilters, DodFilterType<DodMarketFilterType>>[]>([
        {
            label: 'Market Type',
            options: features?.enableLimitedMarketSubscription ? accessibleMarketFolders : [
            {
                key: 'all',
                label: 'All',
                value: { type: 'all' },
            },
            {
                key: 'channels',
                label: 'Channels',
                value: { type: 'channels' },
            },
            {
                key: 'accounts',
                label: 'Accounts',
                value: { type: 'accounts' },
            },
            {
                key: 'stateLines',
                label: 'Statelines',
                value: { type: 'stateLines' },
            },
            {
                key: 'majorMarkets',
                label: 'Major Markets',
                value: { type: 'majorMarkets' },
            },
            {
                key: 'regions',
                label: 'Regions',
                value: { type: 'regions' },
            },
            {
                key: 'divisions',
                label: 'Divisions',
                value: { type: 'divisions' },
            },
            ],
            },
        ]);

    const [characteristicOptionGroups] = useState<DodFilterTypeOptionGroup<DodFilters, DodFilterType<DodMarketFilterType>>[]>([
        {
            label: 'Characteristics',
            options: [
                {
                    key: 'marketType',
                    label: 'Market Type',
                    value: { type: 'marketCharacteristics', data: { id: 'marketType', displayName: 'Market Type' } }
                },
                {
                    key: 'marketLevel',
                    label: 'Market Level',
                    value: { type: 'marketCharacteristics', data: { id: 'marketLevel', displayName: 'Market Level' } }
                },
                {
                    key: 'nationalVsRegional',
                    label: 'National vs Regional',
                    value: { type: 'marketCharacteristics', data: { id: 'marketTier', displayName: 'National vs Regional' } }
                },
                {
                    key: 'parentRetailer',
                    label: 'Parent Retailer',
                    value: { type: 'marketCharacteristics', data: { id: 'parentRetailer', displayName: 'Parent Retailer' } }
                },
                {
                    key: 'channel',
                    label: 'Channel',
                    value: { type: 'marketCharacteristics', data: { id: 'channel', displayName: 'Channel' } }
                },
                {
                    key: 'childMarketType',
                    label: 'Child Market Type',
                    value: { type: 'marketCharacteristics', data: { id: 'childMarketType', displayName: 'Child Market Type' } }
                }
            ],
        },
    ]);

    function handleChange(e) {
        onChange(e);
    }

    function valueMatcher(a: DodFilterType<DodMarketFilterType>, b: DodFilterType<DodMarketFilterType>): boolean {
        return a?.type === b?.type && a?.data?.id === b?.data?.id;
    }

    return (
        <div className={classnames(baseClassName, className)}>
            <ByzzerTabs<MarketTab>
                tabsList={tabList}
                onClick={onTabChange}
                activeTab={activeMarketFolderTab}
                type='default'
            />
            {activeMarketFolderTab === 'markets' && (
                <DodFilterTypePicker
                    className={`${baseClassName}__market-type-picker`}
                    filters={filters}
                    initialOptionGroups={optionGroups}
                    onChange={handleChange}
                    filterPlaceholder={'Search Market Folders'}
                    presetType={'market'}
                    presetGroupLabel={'My Saved Markets'}
                    presetGroupTip={SAVED_MARKET_INFO}
                    showAllButton={false}
                    valueMatcher={valueMatcher}
                    {...props}
                />
            )}
            {activeMarketFolderTab === 'characteristics' && (
                <DodFilterTypePicker
                    className={`${baseClassName}__market-type-picker`}
                    filters={filters}
                    initialOptionGroups={characteristicOptionGroups}
                    onChange={handleChange}
                    filterPlaceholder={'Search Market Folders'}
                    showAllButton={false}
                    valueMatcher={valueMatcher}
                    defaultExpandAll={true}
                    {...props}
                />
            )}
            {activeMarketFolderTab === 'search' && (
                <DodMarketAdvancedSearch value={advancedSearchFilters} onChange={onAdvancedSearchFiltersChange} />
            )}
        </div>
    );
}

export default DodMarketFilterTypePicker;
